/**
 * Estado global do catálogo (PLAN §49).
 *
 * Guarda só o que precisa ser global: metadados da cópia local, status da última
 * verificação e a ação de verificar. **Os dados do catálogo em si continuam no
 * IndexedDB** — duplicar 505 sinais num store em memória seria desperdício e uma
 * segunda fonte de verdade para manter sincronizada.
 */
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { getCatalogMetadata } from '@/db/repositories/catalog';
import { recoverInterruptedJobs } from '@/db/repositories/downloads';
import type { CatalogMetadata } from '@/db/schema';
import { syncCatalog, type SyncSummary } from '@/services/sync/CatalogSyncService';

export type CatalogPhase = 'loading' | 'ready' | 'empty';

interface CatalogContextValue {
  /** `loading` só cobre a leitura local, que é rápida — nunca espera a rede. */
  phase: CatalogPhase;
  metadata: CatalogMetadata | undefined;
  syncing: boolean;
  lastSync: SyncSummary | undefined;
  /** Dispara a verificação manual (F7-T13). */
  checkForUpdates: () => Promise<SyncSummary>;
  dismissSummary: () => void;
}

const CatalogContext = createContext<CatalogContextValue | null>(null);

export function CatalogProvider({ children }: { children: ReactNode }): React.ReactElement {
  const [phase, setPhase] = useState<CatalogPhase>('loading');
  const [metadata, setMetadata] = useState<CatalogMetadata | undefined>(undefined);
  const [syncing, setSyncing] = useState(false);
  const [lastSync, setLastSync] = useState<SyncSummary | undefined>(undefined);

  // Evita duas sincronizações concorrentes (StrictMode monta duas vezes em dev).
  const inFlight = useRef<Promise<SyncSummary> | null>(null);

  const refreshMetadata = useCallback(async () => {
    const current = await getCatalogMetadata();
    setMetadata(current);
    setPhase(current ? 'ready' : 'empty');
    return current;
  }, []);

  const runSync = useCallback(async (): Promise<SyncSummary> => {
    if (inFlight.current) return inFlight.current;

    setSyncing(true);
    const promise = syncCatalog()
      .then(async (summary) => {
        await refreshMetadata();
        setLastSync(summary);
        return summary;
      })
      .finally(() => {
        setSyncing(false);
        inFlight.current = null;
      });

    inFlight.current = promise;
    return promise;
  }, [refreshMetadata]);

  useEffect(() => {
    let cancelled = false;

    void (async () => {
      // 1. Local primeiro: a UI já pode renderizar.
      const current = await refreshMetadata();
      if (cancelled) return;

      // 2. Downloads interrompidos por uma sessão morta voltam para a fila.
      await recoverInterruptedJobs().catch((error) => {
        console.warn('[sync] falha ao recuperar a fila de downloads', error);
      });
      if (cancelled) return;

      // 3. Verificação em segundo plano. Sem catálogo local ela é essencial;
      //    com catálogo local é oportunista e pode falhar em silêncio.
      await runSync().catch((error) => {
        console.warn('[sync] verificação inicial falhou', error);
      });
      void current;
    })();

    return () => {
      cancelled = true;
    };
  }, [refreshMetadata, runSync]);

  const value = useMemo<CatalogContextValue>(
    () => ({
      phase,
      metadata,
      syncing,
      lastSync,
      checkForUpdates: runSync,
      dismissSummary: () => setLastSync(undefined),
    }),
    [phase, metadata, syncing, lastSync, runSync],
  );

  return <CatalogContext.Provider value={value}>{children}</CatalogContext.Provider>;
}

export function useCatalog(): CatalogContextValue {
  const context = useContext(CatalogContext);
  if (!context) throw new Error('useCatalog precisa estar dentro de <CatalogProvider>');
  return context;
}
